import { Fragment, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { CatalogRow } from '@/shared/api/generated/catalog';

import { useConnectionNames } from '@/entities/connection';
import { datasetTone, tracksRenames } from '@/entities/dataset';
import { SCREEN_PATHS } from '@/shared/routes';
import { useCursorStack } from '@/shared/api/paging';
import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { DataTable, PrimaryCell } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { TextField } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Toolbar } from '@/shared/ui/Toolbar';

import { DATASETS_PER_PAGE, usePagedDatasets } from '../api/catalog';
import { useLatestScores } from '../api/scores';
import { groupByConnection } from '../model/grouping';
import type { ConnectionLabel } from '../model/grouping';
import { PiiCell } from './PiiCell';
import { ScoreCell } from './ScoreCell';

const CATALOG_COLUMN_COUNT = 5;

/* The row that heads one connection's datasets.
 *
 * A `<th colSpan>` inside the body rather than a styled `<td>`: it *is* a header for the rows
 * beneath it, and a screen reader announcing it as a data cell would leave a listener with no idea
 * the table had changed source partway down.
 *
 * The name links to that connection's own page, and the link is load-bearing rather than a
 * convenience — a group here covers only the rows on *this* page, so following it is how a reader
 * sees all of that source's datasets rather than the slice this page happened to include.
 */
function GroupHeader({
  connectionId,
  label,
  count,
}: {
  readonly connectionId: string;
  readonly label: ConnectionLabel;
  readonly count: number;
}): ReactElement {
  const { t } = useTranslation();

  /* Never the raw id. Each absent-name case says what is actually true — the connection list is
     still loading, could not be read, or came back without this id — because collapsing them would
     put a claim about the source on screen that only one of the three supports. */
  const name =
    label.kind === 'named' ? (
      <Link to={`${SCREEN_PATHS.connections}/${connectionId}`}>{label.name}</Link>
    ) : (
      <span className="lq-dim">{t(`catalog.group.${label.kind}`)}</span>
    );

  return (
    <tr className="lq-table__group">
      <th scope="colgroup" colSpan={CATALOG_COLUMN_COUNT}>
        <span className="lq-table__group-label">{t('catalog.group.source')}</span>
        {name}
        <span className="lq-table__group-count">
          {t('catalog.group.count', { count })}
        </span>
      </th>
    </tr>
  );
}

export interface CatalogBrowserProps {
  /** Scopes the listing to one connection's datasets. `null` (the default) is the full catalogue —
   * unchanged from before this prop existed, so every existing `<CatalogBrowser />` call site is
   * unaffected. `ConnectionDetail` is the one caller that passes an id, and it does so with `key`
   * set to that same id: a cursor and a search term are only valid for the query they were issued
   * against — `searchFor` below resets the pager for exactly this reason on a search change — and
   * remounting on the id is what gives connectionId the same guarantee without a second reset path.
   */
  readonly connectionId?: string | null;
  /** `false` when embedded inside a page that already has its own heading — `ConnectionDetail`, so
   * far. Only the page-level `PageHeader` is skipped; the search box, table and pager are the same
   * ones the standalone `/datasets` screen renders. The same pattern `ProfilePanel` uses inside
   * `DatasetDetail`: the embedding page owns the heading, the embedded feature owns its body. */
  readonly standalone?: boolean;
}

/* One dataset's cells. Extracted when the table learned to group, so the grouped and the flat
 * renderings are the same row rather than two that can drift — a second copy is how one of them
 * comes to render a score without its coverage. */
function DatasetRowCells({
  dataset,
  scores,
}: {
  readonly dataset: CatalogRow;
  readonly scores: ReturnType<typeof useLatestScores>;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <tr>
      <td>
        <PrimaryCell
          /* Not a badge: no rename tracking is a property of the *source*, not a fault in the
             dataset, and a badge would put it in the same visual language as a real problem. */
          secondary={tracksRenames(dataset) ? undefined : t('catalog.noRenameAnchor')}
        >
          <Link className="lq-cell__primary" to={`/datasets/${dataset.id}`}>
            {/* The schema as a muted prefix rather than part of one string. Thirty rows of
                `audit.` makes the eye re-read the same eight characters on every line; dimming
                them lets it land on what differs. Still one link and still the full qualified
                name to a screen reader, because the two spans are inside it. */}
            <span className="lq-cell__prefix">{dataset.schema_name}.</span>
            {dataset.object_name}
          </Link>
        </PrimaryCell>
      </td>
      <td>
        <ScoreCell summary={scores.data?.get(dataset.id)} unavailable={scores.isError} />
      </td>
      <td>
        <PiiCell row={dataset} />
      </td>
      <td>
        <StatusBadge
          tone={datasetTone(dataset)}
          label={t(`catalog.state.${dataset.state}.label`)}
          title={t(`catalog.state.${dataset.state}.meaning`)}
        />
      </td>
      <td className="lq-table__num lumiq-numeric">{dataset.current_version}</td>
    </tr>
  );
}

/* The catalog browser: search, then a dense table.
 *
 * Server-side search rather than client-side filtering of a fetched page. Filtering in the browser
 * looks identical on a demo tenant with nineteen datasets and silently searches only the first page
 * on a real one with five thousand — the worst kind of bug, because the screen still looks right.
 *
 * **The full catalogue is partitioned by source.** One flat list of every harvested table answers
 * "what exists" and hides "which database is this in" — and two sources may legitimately both hold
 * a `public.employee`, so without the grouping those two rows are indistinguishable.
 */
export function CatalogBrowser({
  connectionId = null,
  standalone = true,
}: CatalogBrowserProps = {}): ReactElement {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const query = useMemo(() => ({ search, connectionId }), [search, connectionId]);
  const pager = useCursorStack();
  const datasets = usePagedDatasets(query, pager.cursor);

  /* **The search resets the pager, and it is required rather than tidy.** A cursor is signed over a
   * fingerprint of the query it was issued for, so carrying one across a search change is refused
   * with a 400 — resetting says the same thing without the round trip, and a position in one result
   * set means nothing in another. */
  function searchFor(next: string): void {
    setSearch(next);
    pager.reset();
  }

  /* Scores are fetched for whatever the catalog returned, in a second request joined by id. The
   * catalog renders without waiting for it: `scores.data` is undefined while in flight and the score
   * column shows an honest blank, rather than the whole table being held back by a call to a
   * different module. See `useLatestScores` for why the join is client-side. */
  const datasetIds = useMemo(
    () => (datasets.data?.items ?? []).map((dataset) => dataset.id),
    [datasets.data],
  );
  const scores = useLatestScores(datasetIds);

  /* Names for the group headers. The same cached read the Connections screen makes, so opening the
   * catalogue after visiting Connections costs nothing and the two cannot name a source
   * differently. Only fetched for the unscoped catalogue — `ConnectionDetail` renders one
   * connection's rows under its own heading and has no groups to label. */
  const connectionNames = useConnectionNames();

  /* `null` means render flat. Grouping is for the full catalogue only: inside `ConnectionDetail`
   * every row is the same source by construction, so a header above each would restate the page. */
  const grouped = useMemo(() => {
    if (connectionId !== null || !datasets.isSuccess) return null;
    return groupByConnection(datasets.data.items, {
      status: connectionNames.isPending
        ? 'loading'
        : connectionNames.isError
          ? 'unreadable'
          : 'ready',
      /* Empty while loading or failed, which is why `status` travels beside it: a caller reading
         emptiness alone would label every group "unlisted" and claim the connections are gone. */
      names: connectionNames.data ?? new Map<string, string>(),
    });
  }, [
    connectionId,
    datasets.isSuccess,
    datasets.data,
    connectionNames.isPending,
    connectionNames.isError,
    connectionNames.data,
  ]);

  return (
    <>
      {standalone ? (
        <PageHeader title={t('catalog.title')} lede={t('catalog.subtitle')} />
      ) : null}

      <Toolbar
        count={
          /* The count on *this page*, not an estate total — the browser now pages, and a keyset
             listing cannot know how many datasets exist without counting the whole table. Inventing
             a total is the `next_cursor: null`-while-truncating defect wearing a denominator. */
          datasets.isSuccess
            ? t('catalog.pageCount', { count: datasets.data.items.length })
            : undefined
        }
      >
        <TextField
          label={t('catalog.searchLabel')}
          type="search"
          value={search}
          onChange={searchFor}
          placeholder={t('catalog.searchPlaceholder')}
        />
      </Toolbar>

      {datasets.isError && (
        <Alert tone="error" title={t('catalog.unreachable')}>
          {datasets.error.message}
        </Alert>
      )}

      {datasets.isPending && (
        <>
          {/* Announced once, politely, rather than by twelve skeleton rows each claiming to be a
              cell. The skeleton itself is aria-hidden. */}
          <p role="status" className="lq-muted">
            {t('catalog.loading')}
          </p>
          <SkeletonRows rows={8} columns={4} />
        </>
      )}

      {datasets.isSuccess &&
        (datasets.data.items.length === 0 ? (
          <EmptyState
            title={
              search.trim().length > 0 ? t('catalog.noMatches') : t('catalog.emptyTitle')
            }
            guidance={
              search.trim().length > 0
                ? t('catalog.noMatchesGuidance', { search: search.trim() })
                : t('catalog.emptyGuidance')
            }
          />
        ) : (
          <Card flush>
            <DataTable
              caption={t('catalog.tableCaption')}
              columns={[
                { key: 'name', label: t('catalog.column.name') },
                /* PII before state. A steward opening this screen is looking for what needs
                   attention, and `active`/`vanished` is almost always `active` — a column of
                   identical values earns its place at the far end, not next to the name. */
                /* Quality first among the signals: it is the number a steward opens this screen
                   for, and PII is the qualifier they check next. */
                { key: 'score', label: t('catalog.column.score') },
                { key: 'pii', label: t('catalog.column.pii') },
                { key: 'state', label: t('catalog.column.state') },
                { key: 'version', label: t('catalog.column.version'), numeric: true },
              ]}
            >
              {/* Grouped on the full catalogue, flat when already scoped to one connection.
                  Repeating a single group header above every row of a page that is by definition
                  one source would be noise stating what the page title already says. */}
              {grouped === null
                ? datasets.data.items.map((dataset) => (
                    <DatasetRowCells key={dataset.id} dataset={dataset} scores={scores} />
                  ))
                : grouped.map((group) => (
                    <Fragment key={group.connectionId}>
                      <GroupHeader
                        connectionId={group.connectionId}
                        label={group.label}
                        count={group.rows.length}
                      />
                      {group.rows.map((dataset) => (
                        <DatasetRowCells
                          key={dataset.id}
                          dataset={dataset}
                          scores={scores}
                        />
                      ))}
                    </Fragment>
                  ))}
            </DataTable>
          </Card>
        ))}

      {/* Rendered whenever the read succeeded, including on a single short page: Back and Next are
          both disabled there, which says "this is all of it" more clearly than an absent control —
          an absent pager reads as a screen that cannot page rather than an estate that fits. */}
      {datasets.isSuccess && datasets.data.items.length > 0 ? (
        <>
          <div className="lq-pager">
            {/* The page number first and the controls pushed right, which is the reference's order.
                A spacer rather than `space-between`, so the label stays hard left when the controls
                wrap under it. */}
            <span>{t('catalog.pager.page', { page: pager.page })}</span>
            <span className="lq-pager__spacer" />
            <button
              type="button"
              className="lq-pager__btn"
              disabled={!pager.canGoBack}
              onClick={pager.back}
            >
              {t('catalog.pager.back')}
            </button>
            {/* Ghost, like Back. The reference gives Next no accent: paging forward is how you read a
                listing rather than a decision about it, and an accent-filled control would be the
                loudest thing on a page whose subject is the rows. */}
            <button
              type="button"
              className="lq-pager__btn"
              disabled={datasets.data.nextCursor == null}
              onClick={() => {
                pager.forward(datasets.data.nextCursor);
              }}
            >
              {t('catalog.pager.next')}
            </button>
          </div>

          {/* The one case a reader could still misread: a page that is exactly full with no next
              page. Without this it looks like Next should work and does not. */}
          {datasets.data.items.length === DATASETS_PER_PAGE &&
          datasets.data.nextCursor == null ? (
            <p className="lq-muted">{t('catalog.pager.exactlyFull')}</p>
          ) : null}
        </>
      ) : null}
    </>
  );
}
